import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Construction,
  AttachMoney,
  CalendarToday,
  CheckBox,
  IndeterminateCheckBox,
  Info,
  Timer,
  Warning
} from '@mui/icons-material';
import milestoneService from '../../services/milestoneService';
import DialogCloseButton from '../common/DialogCloseButton';

/**
 * MilestoneApproval
 * Component for clients to review and approve/reject milestone proposals
 */
const MilestoneApproval = ({ milestone, onStatusChange }) => {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleApprove = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      await milestoneService.approveMilestone(milestone.id);
      setShowApproveDialog(false);
      if (onStatusChange) {
        onStatusChange(milestone.id, 'approved');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve milestone');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      await milestoneService.rejectMilestone(milestone.id, rejectionReason);
      setShowRejectDialog(false);
      if (onStatusChange) {
        // Backend returns the proposal to draft for the contractor to revise
        onStatusChange(milestone.id, 'draft');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject milestone');
    } finally {
      setIsProcessing(false);
    }
  };

  const totalCost = (parseFloat(milestone.laborCost) || 0) + (parseFloat(milestone.materialsCost) || 0);

  return (
    <>
      <Card sx={{ mb: 2, border: '2px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" component="div">
                {milestone.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Submitted by {milestone.contractor_name} on {new Date(milestone.created_at).toLocaleDateString()}
              </Typography>
            </Box>
            <Chip label="Pending Your Approval" color="warning" icon={<Info />} />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Please review this milestone carefully. Once approved, the contractor will be notified and can begin work.
            </Typography>
          </Alert>

          {/* 72-hour auto-release notice */}
          <Alert
            severity="warning"
            icon={<Timer />}
            sx={{ mb: 2, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Important: 72-Hour Review Window
            </Typography>
            <Typography variant="body2">
              When the contractor marks this milestone complete, it will be <strong>automatically approved after 72 hours</strong> unless you raise a dispute. You will receive a notification — act promptly if there is an issue.
            </Typography>
          </Alert>

          {/* Description */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              📋 Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {milestone.description}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Deliverables */}
          {milestone.deliverables && milestone.deliverables.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ✅ Deliverables
              </Typography>
              <List dense>
                {milestone.deliverables.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckBox color="success" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Exclusions */}
          {milestone.exclusions && milestone.exclusions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ❌ Exclusions (Not Included)
              </Typography>
              <List dense>
                {milestone.exclusions.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <IndeterminateCheckBox color="error" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Cost Breakdown */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              💰 Cost Breakdown
            </Typography>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Labor Cost:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  ${parseFloat(milestone.laborCost).toFixed(2)}
                </Typography>
              </Box>
              {milestone.materialsCost > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Materials/Equipment:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    ${parseFloat(milestone.materialsCost).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  ${totalCost.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Timeline */}
          {milestone.timelineDetails && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                📅 Timeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {milestone.timelineDetails.raw || 'Timeline details provided by contractor'}
              </Typography>
            </Box>
          )}

          {/* Additional Notes */}
          {milestone.additionalNotes && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                📝 Additional Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {milestone.additionalNotes}
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Button
            startIcon={<Cancel />}
            onClick={() => setShowRejectDialog(true)}
            disabled={isProcessing}
          >
            Request Changes
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={() => setShowApproveDialog(true)}
            disabled={isProcessing}
          >
            Approve Milestone
          </Button>
        </CardActions>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onClose={() => !isProcessing && setShowApproveDialog(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => !isProcessing && setShowApproveDialog(false)} />
        <DialogTitle>Approve Milestone?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            By approving this milestone, you agree to the scope, deliverables, and cost outlined above.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>What happens next:</strong><br/>
              • The contractor is notified and can begin work<br/>
              • Fund your payment wallet if you haven't already<br/>
              • When the contractor marks work complete, you have <strong>72 hours to dispute</strong> before the milestone is approved automatically
            </Typography>
          </Alert>
          <Alert severity="warning" icon={<Timer />}>
            <Typography variant="body2">
              Make sure to check your notifications when work is submitted — the 72-hour dispute window starts immediately.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApproveDialog(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {isProcessing ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onClose={() => !isProcessing && setShowRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => !isProcessing && setShowRejectDialog(false)} />
        <DialogTitle>Request Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please explain what changes you'd like the contractor to make to this milestone.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Requested Changes"
            placeholder="e.g., Please include waterproofing in deliverables, adjust timeline, etc."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            disabled={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="warning"
            variant="contained"
            disabled={isProcessing || !rejectionReason.trim()}
            startIcon={isProcessing && <CircularProgress size={16} />}
          >
            {isProcessing ? 'Sending...' : 'Request Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MilestoneApproval;
